// ---------------------------------------------------------------------------
// GeoTargetingForm — OPUS-D09 / feature #72
// ---------------------------------------------------------------------------
// Authoring surface for per-campaign geo rules. Lets a tenant pick:
//   - campaignId (loads the existing rule row if any)
//   - mode (allow / deny toggle)
//   - a list of rule tags (country / region / city)
// and save via the /api/v1/campaign-geo-targeting POST upsert.
//
// Uses the feature-local api.ts module (TanStack Query + Sonner). The
// lightweight validator layer mirrors the server regex suite so input shapes
// that would be rejected by the API are refused in the UI first.
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
	GEO_MODES,
	MAX_RULES,
	ruleLabel,
	useGeoTargeting,
	useSaveGeoTargeting,
	validateRuleDraft,
	type GeoMode,
	type GeoRule,
} from "./api";

interface RuleDraft {
	country: string;
	region: string;
	city: string;
}

const EMPTY_DRAFT: RuleDraft = { country: "", region: "", city: "" };

/**
 * Curated ISO-3166 alpha-2 quick-pick — the combobox offers these as a
 * datalist so operators can type or pick. Not exhaustive on purpose; any
 * alpha-2 code can be typed free-form and will be validated on add.
 */
const COUNTRY_SUGGESTIONS: ReadonlyArray<{ code: string; name: string }> = [
	{ code: "US", name: "United States" },
	{ code: "CA", name: "Canada" },
	{ code: "MX", name: "Mexico" },
	{ code: "GB", name: "United Kingdom" },
	{ code: "IE", name: "Ireland" },
	{ code: "DE", name: "Germany" },
	{ code: "FR", name: "France" },
	{ code: "ES", name: "Spain" },
	{ code: "IT", name: "Italy" },
	{ code: "NL", name: "Netherlands" },
	{ code: "SE", name: "Sweden" },
	{ code: "NO", name: "Norway" },
	{ code: "DK", name: "Denmark" },
	{ code: "FI", name: "Finland" },
	{ code: "PL", name: "Poland" },
	{ code: "AU", name: "Australia" },
	{ code: "NZ", name: "New Zealand" },
	{ code: "JP", name: "Japan" },
	{ code: "KR", name: "South Korea" },
	{ code: "SG", name: "Singapore" },
	{ code: "IN", name: "India" },
	{ code: "BR", name: "Brazil" },
	{ code: "AR", name: "Argentina" },
	{ code: "CL", name: "Chile" },
	{ code: "ZA", name: "South Africa" },
];

export function GeoTargetingForm() {
	const [campaignId, setCampaignId] = useState("");
	const [committedCampaignId, setCommittedCampaignId] = useState("");
	const [mode, setMode] = useState<GeoMode>("allow");
	const [rules, setRules] = useState<GeoRule[]>([]);
	const [draft, setDraft] = useState<RuleDraft>(EMPTY_DRAFT);
	const [addError, setAddError] = useState<string | null>(null);
	const [hasLoadedForCampaign, setHasLoadedForCampaign] = useState("");

	const query = useGeoTargeting(committedCampaignId);
	const saver = useSaveGeoTargeting();

	// When a fresh campaignId fetch completes, hydrate the form from the
	// server. Guarded on committedCampaignId so we do not overwrite local
	// edits as the user types a new id.
	useEffect(() => {
		if (!query.isSuccess) return;
		if (committedCampaignId.length === 0) return;
		if (hasLoadedForCampaign === committedCampaignId) return;
		if (query.data) {
			setMode(query.data.mode);
			setRules(query.data.rules);
		} else {
			setMode("allow");
			setRules([]);
		}
		setHasLoadedForCampaign(committedCampaignId);
	}, [
		committedCampaignId,
		hasLoadedForCampaign,
		query.data,
		query.isSuccess,
	]);

	const ruleCount = rules.length;
	const atRuleCap = ruleCount >= MAX_RULES;

	const canSave = useMemo(() => {
		return (
			committedCampaignId.length > 0 &&
			!saver.isPending &&
			// "allow" mode with zero rules would reject every request — block
			// it client-side to prevent an obvious foot-gun.
			!(mode === "allow" && rules.length === 0)
		);
	}, [committedCampaignId, mode, rules.length, saver.isPending]);

	function handleLoad(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		const trimmed = campaignId.trim();
		if (trimmed.length === 0) {
			toast.error("Enter a campaign id to load.");
			return;
		}
		if (!/^[a-z0-9_-]{1,64}$/i.test(trimmed)) {
			toast.error("Campaign id must be 1-64 chars of letters, digits, _ or -.");
			return;
		}
		setCommittedCampaignId(trimmed);
		setHasLoadedForCampaign(""); // force re-hydrate
	}

	function handleAddRule() {
		if (atRuleCap) {
			setAddError(`Cap reached — max ${MAX_RULES} rules per campaign.`);
			return;
		}
		const v = validateRuleDraft(draft);
		if (!v.ok) {
			setAddError(v.error);
			return;
		}
		const key = `${v.rule.country}|${v.rule.region ?? ""}|${v.rule.city ?? ""}`;
		const dup = rules.some(
			(r) => `${r.country}|${r.region ?? ""}|${r.city ?? ""}` === key,
		);
		if (dup) {
			setAddError("That rule is already in the list.");
			return;
		}
		setRules([...rules, v.rule]);
		setDraft(EMPTY_DRAFT);
		setAddError(null);
	}

	function handleRemoveRule(index: number) {
		setRules(rules.filter((_, i) => i !== index));
	}

	function handleSave() {
		if (!committedCampaignId) {
			toast.error("Load a campaign first.");
			return;
		}
		if (mode === "allow" && rules.length === 0) {
			toast.error("Allow mode needs at least one rule, or nothing will pass.");
			return;
		}
		saver.mutate(
			{ campaignId: committedCampaignId, mode, rules },
			{
				onSuccess: (data) => {
					setRules(data.rules);
					setMode(data.mode);
					toast.success(
						`Saved ${data.rules.length} rule${data.rules.length === 1 ? "" : "s"} (${data.mode}).`,
					);
				},
				onError: (err) => {
					toast.error(err.message || "Save failed — try again.");
				},
			},
		);
	}

	const loaded = hasLoadedForCampaign === committedCampaignId && committedCampaignId.length > 0;
	const isLoading = query.isFetching && committedCampaignId.length > 0;

	return (
		<div className="flex flex-col gap-8">
			<CampaignLoader
				campaignId={campaignId}
				onCampaignIdChange={setCampaignId}
				onSubmit={handleLoad}
				loaded={loaded}
				loading={isLoading}
				committedCampaignId={committedCampaignId}
			/>

			{loaded ? (
				<>
					<ModeToggle mode={mode} onChange={setMode} />

					<section className="rounded-[8px] border border-zinc-200 bg-white shadow-brand-subtle">
						<header className="flex items-center justify-between border-b border-zinc-200 px-5 py-4">
							<div>
								<h2 className="text-base font-semibold text-zinc-900">Rules</h2>
								<p className="mt-0.5 text-xs text-zinc-500">
									{ruleCount} of {MAX_RULES} — country, region, or city-level.
								</p>
							</div>
						</header>

						<RuleList rules={rules} onRemove={handleRemoveRule} />

						<AddRuleRow
							draft={draft}
							setDraft={setDraft}
							onAdd={handleAddRule}
							error={addError}
							atCap={atRuleCap}
						/>
					</section>

					<div className="flex items-center justify-end gap-3">
						<span className="text-xs text-zinc-500">
							{mode === "allow"
								? "Allow mode — only the rules above can pass."
								: "Deny mode — all locations pass except the rules above."}
						</span>
						<button
							type="button"
							onClick={handleSave}
							disabled={!canSave}
							className={[
								"inline-flex items-center justify-center rounded-[8px] px-4 py-2 text-sm font-medium",
								"bg-zinc-900 text-white shadow-brand-subtle",
								"hover:bg-zinc-800 disabled:cursor-not-allowed disabled:bg-zinc-300",
							].join(" ")}
						>
							{saver.isPending ? "Saving…" : "Save rules"}
						</button>
					</div>
				</>
			) : null}

			{query.isError && committedCampaignId.length > 0 ? (
				<p className="rounded-[6px] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
					{query.error.message}
				</p>
			) : null}
		</div>
	);
}

// ---- sub-components -------------------------------------------------------

function CampaignLoader(props: {
	campaignId: string;
	onCampaignIdChange: (v: string) => void;
	onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
	loaded: boolean;
	loading: boolean;
	committedCampaignId: string;
}) {
	const { campaignId, onCampaignIdChange, onSubmit, loaded, loading, committedCampaignId } = props;
	return (
		<form
			onSubmit={onSubmit}
			className="rounded-[8px] border border-zinc-200 bg-white p-5 shadow-brand-subtle"
		>
			<label className="block text-sm font-medium text-zinc-800" htmlFor="geo-campaign-id">
				Campaign id
			</label>
			<p className="mt-1 text-xs text-zinc-500">
				Enter the campaign identifier, e.g. <code className="rounded bg-zinc-100 px-1 py-0.5 text-[11px]">summer_us_push</code>.
			</p>
			<div className="mt-3 flex flex-col gap-3 sm:flex-row">
				<input
					id="geo-campaign-id"
					type="text"
					inputMode="text"
					autoComplete="off"
					spellCheck={false}
					value={campaignId}
					onChange={(e) => onCampaignIdChange(e.target.value)}
					placeholder="campaign-abc"
					className={[
						"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900",
						"placeholder:text-zinc-400 focus:border-zinc-500 focus:outline-none",
					].join(" ")}
				/>
				<button
					type="submit"
					disabled={loading}
					className={[
						"inline-flex items-center justify-center rounded-[6px] px-4 py-2 text-sm font-medium",
						"bg-white text-zinc-900 ring-1 ring-inset ring-zinc-300 shadow-brand-subtle",
						"hover:bg-zinc-50 disabled:opacity-60",
					].join(" ")}
				>
					{loading ? "Loading…" : loaded ? "Reload" : "Load"}
				</button>
			</div>
			{committedCampaignId ? (
				<p className="mt-3 text-xs text-zinc-500">
					Editing <span className="font-medium text-zinc-700">{committedCampaignId}</span>.
				</p>
			) : null}
		</form>
	);
}

function ModeToggle(props: { mode: GeoMode; onChange: (m: GeoMode) => void }) {
	const { mode, onChange } = props;
	return (
		<section
			aria-label="Mode"
			className="rounded-[8px] border border-zinc-200 bg-white p-5 shadow-brand-subtle"
		>
			<h2 className="text-base font-semibold text-zinc-900">Mode</h2>
			<p className="mt-1 text-xs text-zinc-500">
				Allow means only the rules pass. Deny means everything passes except the rules.
			</p>
			<div
				role="radiogroup"
				aria-label="Rule mode"
				className="mt-3 inline-flex rounded-[6px] border border-zinc-200 bg-zinc-50 p-0.5"
			>
				{GEO_MODES.map((m) => {
					const active = m === mode;
					return (
						<button
							key={m}
							type="button"
							role="radio"
							aria-checked={active}
							onClick={() => onChange(m)}
							className={[
								"min-w-[88px] rounded-[4px] px-4 py-1.5 text-sm font-medium capitalize transition-colors",
								active
									? "bg-white text-zinc-900 shadow-brand-subtle"
									: "text-zinc-500 hover:text-zinc-800",
							].join(" ")}
						>
							{m}
						</button>
					);
				})}
			</div>
		</section>
	);
}

function RuleList(props: { rules: GeoRule[]; onRemove: (i: number) => void }) {
	const { rules, onRemove } = props;
	if (rules.length === 0) {
		return (
			<div className="px-5 py-6 text-center text-sm text-zinc-500">
				No rules yet — add a country, region, or city below.
			</div>
		);
	}
	return (
		<ul className="divide-y divide-zinc-100">
			{rules.map((rule, i) => (
				<li
					key={`${rule.country}|${rule.region ?? ""}|${rule.city ?? ""}`}
					className="flex items-center justify-between px-5 py-3"
				>
					<div className="flex items-center gap-2">
						<span className={tagClass(ruleTag(rule))}>{ruleTag(rule)}</span>
						<span className="text-sm text-zinc-800">{ruleLabel(rule)}</span>
						<span className="text-xs text-zinc-400">
							{rule.country}
							{rule.region ? ` · ${rule.region}` : ""}
							{rule.city ? ` · ${rule.city}` : ""}
						</span>
					</div>
					<button
						type="button"
						onClick={() => onRemove(i)}
						className="rounded-[6px] px-2 py-1 text-xs font-medium text-zinc-500 hover:bg-zinc-50 hover:text-zinc-800"
						aria-label={`Remove ${ruleLabel(rule)}`}
					>
						Remove
					</button>
				</li>
			))}
		</ul>
	);
}

function AddRuleRow(props: {
	draft: RuleDraft;
	setDraft: (d: RuleDraft) => void;
	onAdd: () => void;
	error: string | null;
	atCap: boolean;
}) {
	const { draft, setDraft, onAdd, error, atCap } = props;
	return (
		<div className="border-t border-zinc-100 bg-zinc-50 px-5 py-4">
			<div className="grid grid-cols-1 gap-3 sm:grid-cols-[140px_160px_1fr_auto] sm:items-end">
				<Field label="Country (ISO)" id="geo-country">
					<input
						id="geo-country"
						list="geo-country-suggestions"
						type="text"
						maxLength={2}
						value={draft.country}
						onChange={(e) =>
							setDraft({ ...draft, country: e.target.value.toUpperCase() })
						}
						placeholder="US"
						className={inputClass}
					/>
					<datalist id="geo-country-suggestions">
						{COUNTRY_SUGGESTIONS.map((c) => (
							<option key={c.code} value={c.code}>
								{c.name}
							</option>
						))}
					</datalist>
				</Field>
				<Field label="Region (ISO-2)" id="geo-region">
					<input
						id="geo-region"
						type="text"
						value={draft.region}
						onChange={(e) =>
							setDraft({ ...draft, region: e.target.value.toUpperCase() })
						}
						placeholder="US-CA"
						className={inputClass}
					/>
				</Field>
				<Field label="City (optional)" id="geo-city">
					<input
						id="geo-city"
						type="text"
						maxLength={80}
						value={draft.city}
						onChange={(e) => setDraft({ ...draft, city: e.target.value })}
						placeholder="San Francisco"
						className={inputClass}
					/>
				</Field>
				<button
					type="button"
					onClick={onAdd}
					disabled={atCap}
					className={[
						"h-[38px] rounded-[6px] px-4 text-sm font-medium",
						"bg-emerald-600 text-white shadow-brand-subtle hover:bg-emerald-700",
						"disabled:cursor-not-allowed disabled:bg-zinc-300",
					].join(" ")}
				>
					Add rule
				</button>
			</div>
			{error ? (
				<p className="mt-3 text-xs text-red-600" role="alert">
					{error}
				</p>
			) : null}
			{atCap ? (
				<p className="mt-3 text-xs text-amber-700">
					Cap reached ({MAX_RULES}). Remove a rule to add another.
				</p>
			) : null}
		</div>
	);
}

function Field(props: { label: string; id: string; children: React.ReactNode }) {
	return (
		<label className="block text-xs font-medium text-zinc-600" htmlFor={props.id}>
			<span className="mb-1 block">{props.label}</span>
			{props.children}
		</label>
	);
}

function ruleTag(rule: GeoRule): "country" | "region" | "city" {
	if (rule.city) return "city";
	if (rule.region) return "region";
	return "country";
}

function tagClass(kind: "country" | "region" | "city"): string {
	const base =
		"inline-flex items-center rounded-[6px] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide";
	if (kind === "city") return `${base} bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200`;
	if (kind === "region")
		return `${base} bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200`;
	return `${base} bg-zinc-100 text-zinc-700 ring-1 ring-inset ring-zinc-200`;
}

const inputClass = [
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900",
	"placeholder:text-zinc-400 focus:border-zinc-500 focus:outline-none",
].join(" ");
